// src/components/checkStoreAvailability.js

/**
 * Check if a store is currently open based on its week_schedule
 * @param {Object} weekSchedule - The week_schedule object from branch data
 * @returns {Object} { isOpen: boolean, message: string, nextOpenTime: string|null }
 */
export const checkStoreAvailability = (weekSchedule) => {
  if (!weekSchedule) {
    return {
      isOpen: false,
      message: "Store schedule not available",
      nextOpenTime: null
    };
  }

  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[now.getDay()].toLowerCase();
  const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

  const todaySchedule = weekSchedule[currentDay];

  if (!todaySchedule) {
    return {
      isOpen: false,
      message: "Store schedule not configured for today",
      nextOpenTime: null
    };
  }

  // Check if store is marked as closed for the day
  if (todaySchedule.is_open === false) {
    const nextOpenDay = findNextOpenDay(weekSchedule, currentDay, dayNames);
    return {
      isOpen: false,
      message: `Store is closed on ${capitalize(currentDay)}s`,
      nextOpenTime: nextOpenDay ? `Opens ${nextOpenDay}` : null
    };
  }

  // Handle schedules array (new format with multiple time slots)
  if (todaySchedule.schedules && Array.isArray(todaySchedule.schedules)) {
    for (const schedule of todaySchedule.schedules) {
      const openTime = timeToMinutes(schedule.open_time);
      const closeTime = timeToMinutes(schedule.close_time);

      if (currentTime >= openTime && currentTime < closeTime) {
        return {
          isOpen: true,
          message: `Open until ${formatTime(schedule.close_time)}`,
          closeTime: schedule.close_time
        };
      }
    }

    // If not currently open, find next opening time
    const nextSlot = todaySchedule.schedules.find(s => 
      timeToMinutes(s.open_time) > currentTime
    );

    if (nextSlot) {
      return {
        isOpen: false,
        message: `Closed now. Opens at ${formatTime(nextSlot.open_time)}`,
        nextOpenTime: formatTime(nextSlot.open_time)
      };
    }

    // Check tomorrow
    const tomorrowDay = dayNames[(now.getDay() + 1) % 7];
    const tomorrowSchedule = weekSchedule[tomorrowDay];
    
    if (tomorrowSchedule?.is_open && tomorrowSchedule.schedules?.[0]) {
      return {
        isOpen: false,
        message: `Closed today. Opens tomorrow at ${formatTime(tomorrowSchedule.schedules[0].open_time)}`,
        nextOpenTime: `Tomorrow at ${formatTime(tomorrowSchedule.schedules[0].open_time)}`
      };
    }

    return {
      isOpen: false,
      message: "Store is currently closed",
      nextOpenTime: null
    };
  }

  // Handle old format (single open_time and close_time)
  if (todaySchedule.open_time && todaySchedule.close_time) {
    const openTime = timeToMinutes(todaySchedule.open_time);
    const closeTime = timeToMinutes(todaySchedule.close_time);

    if (currentTime >= openTime && currentTime < closeTime) {
      return {
        isOpen: true,
        message: `Open until ${formatTime(todaySchedule.close_time)}`,
        closeTime: todaySchedule.close_time
      };
    }

    if (currentTime < openTime) {
      return {
        isOpen: false,
        message: `Closed now. Opens at ${formatTime(todaySchedule.open_time)}`,
        nextOpenTime: formatTime(todaySchedule.open_time)
      };
    }

    // After closing time - check tomorrow
    const tomorrowDay = dayNames[(now.getDay() + 1) % 7];
    const tomorrowSchedule = weekSchedule[tomorrowDay];
    
    if (tomorrowSchedule?.is_open && tomorrowSchedule.open_time) {
      return {
        isOpen: false,
        message: `Closed today. Opens tomorrow at ${formatTime(tomorrowSchedule.open_time)}`,
        nextOpenTime: `Tomorrow at ${formatTime(tomorrowSchedule.open_time)}`
      };
    }
  }

  return {
    isOpen: false,
    message: "Store is currently closed",
    nextOpenTime: null
  };
};

/**
 * Convert time string (HH:MM) to minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Format time string for display (HH:MM to 12-hour format)
 */
const formatTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Find the next day the store is open
 */
const findNextOpenDay = (weekSchedule, currentDay, dayNames) => {
  const currentIndex = dayNames.indexOf(currentDay);
  
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentIndex + i) % 7;
    const nextDay = dayNames[nextDayIndex];
    const schedule = weekSchedule[nextDay];
    
    if (schedule?.is_open) {
      const openTime = schedule.schedules?.[0]?.open_time || schedule.open_time;
      if (openTime) {
        return `${capitalize(nextDay)} at ${formatTime(openTime)}`;
      }
    }
  }
  
  return null;
};

/**
 * Capitalize first letter
 */
const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get full store status with detailed information
 */
export const getStoreStatus = (weekSchedule) => {
  const availability = checkStoreAvailability(weekSchedule);
  const now = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const currentDayName = dayNames[now.getDay()];
  
  return {
    ...availability,
    currentDay: currentDayName,
    currentTime: now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  };
};

/**
 * Get today's schedule in a readable format
 */
export const getTodaySchedule = (weekSchedule) => {
  if (!weekSchedule) return null;
  
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[new Date().getDay()].toLowerCase();
  const todaySchedule = weekSchedule[currentDay];
  
  if (!todaySchedule || !todaySchedule.is_open) {
    return null;
  }
  
  if (todaySchedule.schedules && Array.isArray(todaySchedule.schedules)) {
    return todaySchedule.schedules.map(s => 
      `${formatTime(s.open_time)} - ${formatTime(s.close_time)}`
    ).join(', ');
  }
  
  if (todaySchedule.open_time && todaySchedule.close_time) {
    return `${formatTime(todaySchedule.open_time)} - ${formatTime(todaySchedule.close_time)}`;
  }
  
  return null;
};